import { UseMutationOptions, UseQueryOptions, useMutation, useQueries, useQuery } from '@tanstack/react-query';
import { AddContactCompany, Employee, BasicCompany, Company, VoyagerCompany } from 'src/types';
import { queryClient } from '..';
import {
  ADD_CONTACT_COMPANY_KEY,
  EMPLOYEE_QUERY_KEY,
  BASIC_COMPANY_KEY,
  COMPANY_QUERY_KEY,
  COMPANY_WITH_EMPLOYEE_PREFERENCES_QUERY_KEY,
  DELETE_KEY,
  ENTITY_KEY,
  FIRST_COMPANY_KEY,
  LIST_KEY,
  POST_KEY,
  PUT_KEY,
} from '../queryKeys';
import {
  invalidateIfAllMutationsDone,
  queryYemboApiCall,
  queryYemboApiCallWithReturnValue,
} from '../queryYemboApiCalls';

import { invalidateAllCompanyEmployeesListQueries } from '../utils';

/**
 * A company as `GET /company/:key` answers it. `Company` is the moving-side
 * shape inherited from the fork; this adds the claims-side fields read from it.
 */
export type CompanyRecord = Company &
  Pick<VoyagerCompany, 'availableActions' | 'preferredStores' | 'bannedStores' | 'minimumRepairValue'>;

export type PartialCompany = Partial<Company> & Pick<Company, 'key'>;
type UseCompanyOptions = Partial<UseQueryOptions<CompanyRecord | null, unknown, CompanyRecord>>;
type PutCompanyOptions = UseMutationOptions<
  Company,
  unknown,
  { company: PartialCompany; password?: string },
  Company | undefined
>;

const URL = 'company';

/** `GET /company/:key` answers the company itself. */
export const getCompany = async (companyKey: string): Promise<CompanyRecord | null> =>
  await queryYemboApiCallWithReturnValue<CompanyRecord>({
    url: `${URL}/${companyKey}`,
    shouldNotifyAndThrowError: true,
  });

export const getCompanyQueryOptions = (companyKey: string | undefined | null, options?: Partial<UseCompanyOptions>) => {
  return {
    queryKey: [COMPANY_QUERY_KEY, ENTITY_KEY, companyKey],
    queryFn: () => getCompany(companyKey ?? ''),
    ...options,
    enabled: options?.enabled !== false && !!companyKey,
  };
};

export const useCompanyQuery = (companyKey: string | undefined | null, options?: Partial<UseCompanyOptions>) =>
  useQuery(getCompanyQueryOptions(companyKey, options));

export const useCompaniesQuery = (companyKeys: string[], options?: Partial<UseCompanyOptions>) =>
  useQueries({
    queries: companyKeys.map((key) => getCompanyQueryOptions(key, options)),
    combine: (results) => ({
      data: results.map((result) => result.data),
      error: results.find((result) => result.error),
      isLoading: results.some((result) => result.isLoading),
    }),
  });

// service-voyager-api updates a company at `PUT /company/:key`; there is no
// keyless `PUT /company`.
const putCompany = async (company: PartialCompany, password?: string) => {
  const response = await queryYemboApiCall({
    url: `${URL}/${company.key}`,
    method: 'PUT',
    body: JSON.stringify({ ...company, password }),
    logoutIfNotAuthorized: false,
    shouldNotifyAndThrowError: true,
  });

  return (await response.json()) as Company;
};

export const usePutCompany = (options?: PutCompanyOptions) => {
  const mutationKey = [COMPANY_QUERY_KEY, PUT_KEY];

  return useMutation<Company, unknown, { company: PartialCompany; password?: string }, Company | undefined>({
    mutationKey,
    mutationFn: ({ company, password }) => putCompany(company, password),
    ...options,
    onMutate: async (variables, context) => {
      const queryKey = [COMPANY_QUERY_KEY, ENTITY_KEY, variables.company.key];

      await queryClient.cancelQueries({ queryKey: [COMPANY_QUERY_KEY] });

      const previousCompany = queryClient.getQueryData<Company>(queryKey);

      if (previousCompany)
        queryClient.setQueryData<Company>(queryKey, {
          ...previousCompany,
          ...variables.company,
        });

      options?.onMutate?.(variables, context);

      return previousCompany;
    },
    onError: (error, variables, onMutateResult, context) => {
      const queryKey = [COMPANY_QUERY_KEY, ENTITY_KEY, variables.company.key];

      if (onMutateResult) queryClient.setQueryData<Company>(queryKey, onMutateResult);

      options?.onError?.(error, variables, onMutateResult, context);
    },
    onSettled: (response, error, variables, onMutateResult, context) => {
      invalidateIfAllMutationsDone([COMPANY_QUERY_KEY, ENTITY_KEY, variables.company.key], mutationKey);
      invalidateIfAllMutationsDone([COMPANY_QUERY_KEY, LIST_KEY], mutationKey);

      // Invalidate all the employees of the company
      queryClient.invalidateQueries({
        queryKey: [EMPLOYEE_QUERY_KEY, ENTITY_KEY],
        predicate: (query) =>
          !!queryClient.getQueryData<Employee>(query.queryKey)?.companyKeys?.includes(variables.company.key),
      });

      options?.onSettled?.(response, error, variables, onMutateResult, context);
    },
    onSuccess: (...params) => {
      options?.onSuccess?.(...params);

      const [, { company: updatedCompany }, prevCompany] = params;
      const companyKey = updatedCompany.key;
      const isEmployeePreferencesEnabledNew = updatedCompany.isEmployeePreferencesEnabled;
      const isEmployeePreferencesEnabledOld = prevCompany?.isEmployeePreferencesEnabled;

      const hasIsEmployeePreferencesEnabledChanged =
        isEmployeePreferencesEnabledNew !== undefined &&
        isEmployeePreferencesEnabledOld !== undefined &&
        isEmployeePreferencesEnabledNew !== isEmployeePreferencesEnabledOld;

      if (hasIsEmployeePreferencesEnabledChanged) {
        invalidateAllCompanyEmployeesListQueries('room-option', companyKey);

        queryClient.invalidateQueries({
          queryKey: [COMPANY_QUERY_KEY, COMPANY_WITH_EMPLOYEE_PREFERENCES_QUERY_KEY],
        });
      }
    },
  });
};

// `GET /company` is not paged: it answers every company the caller may read, newest first.
const getFirstCompany = async () => {
  const companies = await queryYemboApiCallWithReturnValue<Company[], 'companies'>({
    url: URL,
    key: 'companies',
    shouldNotifyAndThrowError: true,
    errorOnNotFound: true,
  });

  const company = companies?.[0];
  if (!company) throw new Error('No company found for auth user');
  return company;
};

export const useFirstCompanyKeyQuery = (options?: Partial<UseQueryOptions<string>>) => {
  return useQuery({
    queryKey: [COMPANY_QUERY_KEY, FIRST_COMPANY_KEY],
    queryFn: async () => {
      const company = await getFirstCompany();
      queryClient.setQueryData([COMPANY_QUERY_KEY, ENTITY_KEY, company.key], company);
      return company.key;
    },
    ...options,
  });
};

const basicCompanyFields: (keyof BasicCompany)[] = ['key', 'name', 'parentCompanyKey', 'timezone'] as const;

const getAllCompanies = async <T = BasicCompany>(signal?: AbortSignal, fields: string[] = basicCompanyFields) => {
  const companies = await queryYemboApiCallWithReturnValue<T[], 'companies'>({
    url: URL,
    urlParams: new URLSearchParams({
      fields: JSON.stringify(fields),
    }),
    key: 'companies',
    shouldNotifyAndThrowError: true,
    signal,
  });

  if (!companies) throw new Error('No companies found');
  return companies;
};

type AllBasicCompaniesQueryOptions = UseQueryOptions<BasicCompany[]>;

export const getAllBasicCompaniesQueryOptions = (): AllBasicCompaniesQueryOptions => {
  return {
    queryKey: [COMPANY_QUERY_KEY, LIST_KEY, BASIC_COMPANY_KEY],
    queryFn: ({ signal }) => getAllCompanies(signal),
    staleTime: 10 * 60 * 1000, // 10 min because the query may be heavy
  };
};

export const useAllBasicCompaniesQuery = (options?: Partial<AllBasicCompaniesQueryOptions>) =>
  useQuery({
    ...getAllBasicCompaniesQueryOptions(),
    ...options,
  });

const addContactCompanyFields: (keyof AddContactCompany)[] = [
  'key',
  'name',
  'defaultCountry',
  'defaultSurveyType',
  'agentCode',
  'partnerCompanyKeys',
  'parentCompanyKey',
  'vanLine',
  'businessUnits',
  'subsetSurveyAgentCodes',
  'defaultLocale',
  'isJobsEnabled',
] as const;

type AllAddContactCompaniesQueryOptions = UseQueryOptions<AddContactCompany[]>;

export const useAllAddContactCompaniesQuery = (options?: Partial<AllAddContactCompaniesQueryOptions>) =>
  useQuery({
    queryKey: [COMPANY_QUERY_KEY, LIST_KEY, ADD_CONTACT_COMPANY_KEY],
    queryFn: ({ signal }) => getAllCompanies<AddContactCompany>(signal, addContactCompanyFields),
    staleTime: 10 * 60 * 1000, // 10 min because the query may be heavy
    ...options,
  });

type KeyWithPasswordCompanyOptions = UseMutationOptions<Response, unknown, { key: string; password?: string }>;

export const useDeleteCompany = (options?: KeyWithPasswordCompanyOptions) => {
  const mutationKey = [COMPANY_QUERY_KEY, DELETE_KEY];

  return useMutation({
    mutationKey,
    mutationFn: ({ key }) =>
      queryYemboApiCall({
        method: 'DELETE',
        url: `${URL}/${key}`,
        shouldNotifyAndThrowError: true,
        logoutIfNotAuthorized: false,
      }),
    ...options,
    onSuccess: (...params) => {
      const [, variables] = params;

      const { key: companyKey } = variables;

      queryClient.removeQueries({
        queryKey: [COMPANY_QUERY_KEY, ENTITY_KEY, companyKey],
      });
      queryClient.invalidateQueries({
        queryKey: [COMPANY_QUERY_KEY, LIST_KEY],
      });
      invalidateAllCompanyEmployeesListQueries('room-option', companyKey);

      queryClient.invalidateQueries({
        queryKey: [COMPANY_QUERY_KEY, COMPANY_WITH_EMPLOYEE_PREFERENCES_QUERY_KEY],
      });

      options?.onSuccess?.(...params);
    },
  });
};

export const useResetCompany = (options?: KeyWithPasswordCompanyOptions) => {
  const mutationKey = [COMPANY_QUERY_KEY, PUT_KEY];

  return useMutation({
    mutationKey,
    mutationFn: ({ key, password }) =>
      queryYemboApiCall({
        method: 'PUT',
        url: `${URL}/reset`,
        body: JSON.stringify({ key, password }),
        shouldNotifyAndThrowError: true,
        logoutIfNotAuthorized: false,
      }),
    ...options,
    onSettled: (response, error, variables, onMutateResult, context) => {
      invalidateIfAllMutationsDone([COMPANY_QUERY_KEY, ENTITY_KEY, variables.key], mutationKey);
      invalidateIfAllMutationsDone([COMPANY_QUERY_KEY, LIST_KEY], mutationKey);
      options?.onSettled?.(response, error, variables, onMutateResult, context);
    },
  });
};

type PostCompanyOptions = UseMutationOptions<Response, unknown, { company: PartialCompany; password?: string }>;

export const usePostCompany = (options?: PostCompanyOptions) => {
  return useMutation({
    mutationKey: [COMPANY_QUERY_KEY, POST_KEY],
    mutationFn: ({ company, password }) =>
      queryYemboApiCall({
        method: 'POST',
        url: URL,
        body: JSON.stringify({ ...company, password }),
        shouldNotifyAndThrowError: true,
        logoutIfNotAuthorized: false,
      }),
    ...options,
    onSuccess: (response, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({
        queryKey: [COMPANY_QUERY_KEY, LIST_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [COMPANY_QUERY_KEY, COMPANY_WITH_EMPLOYEE_PREFERENCES_QUERY_KEY],
      });
      options?.onSuccess?.(response, variables, onMutateResult, context);
    },
  });
};

const getCompanyBillingPortal = async (key: string): Promise<string | null> => {
  const response = await queryYemboApiCallWithReturnValue<string[] | null, 'url'>({
    url: `${URL}/zenskar-portal`,
    key: 'url',
    urlParams: new URLSearchParams({ key }),
  });

  return response?.[0] ?? null;
};

const getCompanyBillingPortalQueryOptions = (key: string | undefined | null) => {
  return {
    queryKey: ['companyBillingPortal', key],
    queryFn: () => getCompanyBillingPortal(key ?? ''),
    enabled: !!key,
  };
};

export const useCompanyBillingPortalQuery = (key: string | undefined | null) =>
  useQuery(getCompanyBillingPortalQueryOptions(key));

type CompaniesWithEmpPrefsOptions = UseQueryOptions<BasicCompany[], Error>;

const getCompaniesWithEmployeePreferences = async (fields: string[] = basicCompanyFields) => {
  const companies = await queryYemboApiCallWithReturnValue<BasicCompany[], 'companies'>({
    url: URL,
    key: 'companies',
    urlParams: new URLSearchParams({
      fields: JSON.stringify(fields),
    }),
    shouldNotifyAndThrowError: true,
  });

  return companies ?? [];
};

export const useCompaniesWithEmployeePreferencesQuery = (options?: Partial<CompaniesWithEmpPrefsOptions>) =>
  useQuery({
    ...options,
    queryKey: [COMPANY_QUERY_KEY, COMPANY_WITH_EMPLOYEE_PREFERENCES_QUERY_KEY],
    queryFn: () => getCompaniesWithEmployeePreferences(),
  });
